import { generateUUID } from 'store/functions/geoJsonFunctions.js';

/*
Which saved scenario is open: the one rule, in one place.

THE ID LIVES IN ONE PLACE: generalSettings.scenario.scenarioId

It used to live in two. saveScenario overwrote by a top-level
generalSettings.scenarioId that only leaving example mode ever wrote, while a
save, a load and Rename all used the nested one. So:
  - Save on a new or loaded scenario minted a fresh id every time. Each press
    stored a duplicate, used up a saved-scenario slot, and a free user at the
    quota got a 403 for saving a scenario they already had.
  - After leaving an example, Save New followed by Save wrote the second
    scenario's content and name over the FIRST one.

THE STORED BODY NAMES ITSELF

A save posts the whole scenario state, and that state was captured before the
save succeeded, so every stored body embedded the PREVIOUS save's id (and any
top-level id). A load kept whatever id the body named. A loaded scenario could
therefore point at a different one of the user's scenarios, and Rename, which
posts the whole state to /update_instance under that id, replaced that other
scenario's content and name. The body is now stamped with the id and name it is
saved under, and a load believes the id it was loaded BY, never the body.

An example is never the user's scenario: loading one leaves no id, so the first
Save creates the user's own copy instead of meeting the ownership 403.
*/

/**
 * Marks the open scenario whose id the version-2 persisted-state migration
 * forgot, so its next plain Save, which necessarily makes a new copy, can say
 * so once. See forgetPersistedScenarioIds. Lives on generalSettings.scenario,
 * which already persists; cleared by the next save of any kind and by leaving
 * example mode, never written to a stored scenario, and absent on a load (the
 * loaded body replaces generalSettings).
 */
export const ID_CLEARED_BY_UPDATE = 'scenarioIdClearedByUpdate';

/** Shown once per browser, on the first plain Save after the migration forgot the open scenario's id. */
export const SAVED_AS_NEW_AFTER_UPDATE_MESSAGE =
  "Saved as a new scenario. Because of a recent update, this save couldn't replace your earlier copy. If you have an older copy in Saved scenarios you no longer need, you can delete it.";

/**
 * Whether saving this state will make a new copy only because the update forgot
 * the open scenario's id: a plain Save (overwrite), or a Rename of the open
 * scenario, which saves a new copy when there is no id to rename.
 *
 * @param {object} present - inputData.present
 * @param {boolean} replacesOpenScenario - overwrite, or a rename of the open scenario
 * @returns {boolean}
 */
export function savesAsNewBecauseOfUpdate(present, replacesOpenScenario) {
  const scenario = present?.generalSettings?.scenario;
  return Boolean(replacesOpenScenario && scenario && scenario[ID_CLEARED_BY_UPDATE] === true && !scenario.scenarioId);
}

/*
WHICH OPEN SCENARIO A REQUEST BELONGS TO

A save or rename is a round trip, and the open scenario can change while it is
out: load another scenario, open an example. Its response must only be applied to
the scenario it was sent for, or the other scenario inherits this one's id and its
next Save writes over this one.

The open scenario is identified by its id together with scenarioChange, the value
a load (fetchSavedScenario.fulfilled) sets fresh each time. The id alone cannot
tell two unsaved scenarios apart (both null); scenarioChange alone cannot tell a
scenario before and after its first save apart. Both, captured when the request
starts, can. The same pair picks out the undo-history snapshots of that scenario:
see stampScenarioHistory.
*/

/**
 * @param {object} snapshot - an inputData.present (or an undo-history entry)
 * @returns {{scenarioId: string|null, scenarioChange: string|null}}
 */
export function scenarioIdentityOf(snapshot) {
  const scenario = snapshot?.generalSettings?.scenario || {};
  return {
    scenarioId: scenario.scenarioId ?? null,
    scenarioChange: scenario.scenarioChange ?? null,
  };
}

/**
 * @param {object} snapshot
 * @param {{scenarioId: string|null, scenarioChange: string|null}|undefined} identity
 * @returns {boolean}
 */
export function isSameOpenScenario(snapshot, identity) {
  if (!identity) return false;
  const current = scenarioIdentityOf(snapshot);
  return current.scenarioId === (identity.scenarioId ?? null)
    && current.scenarioChange === (identity.scenarioChange ?? null);
}

const stampSnapshot = (snapshot, identity, { scenarioId, scenarioName }) => {
  if (!snapshot || !isSameOpenScenario(snapshot, identity)) return snapshot;
  const scenario = { ...snapshot.generalSettings.scenario, scenarioId, scenarioName };
  delete scenario[ID_CLEARED_BY_UPDATE];
  return { ...snapshot, generalSettings: { ...snapshot.generalSettings, scenario } };
};

/**
 * After a save or rename of a scenario succeeds, give its undo-history snapshots
 * (past, future, and redux-undo's _latestUnfiltered, which undo pushes onto
 * future) the id and name it was just saved under.
 *
 * Without this, Undo after a save brings back a snapshot from before it: a new
 * scenario's null id (so the next Save makes a duplicate) or, after Save New, the
 * ORIGINAL scenario's id (so the next Save writes the copy's edits over the
 * original). Only snapshots of the same open scenario are touched (same id and
 * scenarioChange as when the request started), so an id never crosses a load.
 * present is not touched here; the slice's fulfilled case applies the same rule
 * to it. Snapshots are frozen, so changed ones are copied.
 *
 * @param {object} history - the redux-undo state
 * @param {{scenarioId: string|null, scenarioChange: string|null}} identity - captured when the request started
 * @param {{scenarioId: string, scenarioName: string}} stamp
 * @returns {object} the same history if nothing matched
 */
export function stampScenarioHistory(history, identity, stamp) {
  if (!history || !identity || !stamp?.scenarioId) return history;
  let changed = false;
  const stampAll = (list) => {
    if (!Array.isArray(list)) return list;
    const next = list.map((snapshot) => stampSnapshot(snapshot, identity, stamp));
    if (next.some((snapshot, index) => snapshot !== list[index])) { changed = true; return next; }
    return list;
  };
  const past = stampAll(history.past);
  const future = stampAll(history.future);
  const latest = stampSnapshot(history._latestUnfiltered, identity, stamp);
  if (latest !== history._latestUnfiltered) changed = true;
  return changed ? { ...history, past, future, _latestUnfiltered: latest } : history;
}

/**
 * The id a save writes to: the open scenario's own id when overwriting it,
 * otherwise a new one.
 *
 * @param {object} present - inputData.present
 * @param {boolean} overwrite
 * @returns {string}
 */
export function scenarioIdForSave(present, overwrite) {
  const current = present?.generalSettings?.scenario?.scenarioId;
  return overwrite && current ? current : generateUUID();
}

/**
 * The scenario state as it should be stored under `scenarioId`: stamped with
 * that id and name, and with the retired top-level id removed. Does not mutate.
 *
 * @param {object} present - inputData.present
 * @param {string} scenarioId
 * @param {string} scenarioName
 * @returns {object}
 */
export function withScenarioIdentity(present, scenarioId, scenarioName) {
  const generalSettings = { ...(present.generalSettings || {}) };
  delete generalSettings.scenarioId;
  generalSettings.scenario = { ...(generalSettings.scenario || {}), scenarioId, scenarioName };
  // A browser-local marker, not part of a scenario.
  delete generalSettings.scenario[ID_CLEARED_BY_UPDATE];
  return { ...present, generalSettings };
}

/**
 * Whether a fetchSavedScenario request (or its fulfilled payload) loads a
 * published example rather than the user's own scenario.
 *
 * Decided by the request alone: an exampleSlug (the gallery), "build from
 * scratch" on an example card, or a load by name, which the backend resolves as an
 * example slug. NOT by the mode the app happened to be in: someone who last viewed
 * an example and then opens their own scenario from the Account page is loading
 * their own scenario, and reading example-ness from the stale mode made it lose
 * its id, so its next Save created a second copy.
 *
 * @param {{scenarioId?: string, scenarioName?: string, exampleSlug?: string|null, fromScratch?: boolean}} request
 * @returns {boolean}
 */
export function isExampleLoad(request) {
  if (!request) return false;
  return Boolean(request.exampleSlug) || Boolean(request.fromScratch)
    || (!request.scenarioId && Boolean(request.scenarioName));
}

/**
 * The id a loaded scenario is known by afterwards: the id it was loaded BY, for
 * the user's own scenario, and null for an example (gallery, slug or "build from
 * scratch"), which the user does not own.
 *
 * @param {{scenarioId?: string, scenarioName?: string, exampleSlug?: string|null, fromScratch?: boolean}} payload
 *   fetchSavedScenario's fulfilled payload.
 * @returns {string|null}
 */
export function scenarioIdAfterLoad(payload) {
  if (!payload || isExampleLoad(payload)) return null;
  return typeof payload.scenarioId === 'string' && payload.scenarioId ? payload.scenarioId : null;
}

/**
 * Persisted-state migration: remove the retired top-level id and forget the
 * nested one.
 *
 * Forgetting the nested id is deliberate. A stored id can be wrong in exactly
 * the way this fix is about (a loaded scenario that names an older, different
 * scenario), and there is no way to tell from the browser. Keeping it would let
 * the corrected Save or Rename write over that other scenario, where the old
 * code happened to make a new one. Forgetting it means the next Save of an open,
 * already-saved scenario makes one new copy, once per browser: a duplicate, not
 * a loss. That scenario is marked (ID_CLEARED_BY_UPDATE) so the Save can say so.
 * The marker has to be written here: after rehydration nothing can tell a
 * scenario whose id was forgotten from one that was never saved.
 *
 * @param {object|undefined} persisted - the persisted inputData slice (redux-undo shape)
 * @returns {object|undefined}
 */
export function forgetPersistedScenarioIds(persisted) {
  if (!persisted || typeof persisted !== 'object') return persisted;
  const fix = (snapshot) => {
    if (!snapshot || typeof snapshot !== 'object' || !snapshot.generalSettings) return snapshot;
    const generalSettings = { ...snapshot.generalSettings };
    delete generalSettings.scenarioId;
    if (generalSettings.scenario && typeof generalSettings.scenario === 'object') {
      const hadId = typeof generalSettings.scenario.scenarioId === 'string' && generalSettings.scenario.scenarioId !== '';
      generalSettings.scenario = { ...generalSettings.scenario, scenarioId: null };
      // Only a scenario that really lost an id is marked, so a brand-new,
      // never-saved scenario gets no notice. See ID_CLEARED_BY_UPDATE.
      if (hadId) generalSettings.scenario[ID_CLEARED_BY_UPDATE] = true;
    }
    return { ...snapshot, generalSettings };
  };
  const next = { ...persisted };
  if ('present' in next) next.present = fix(next.present);
  if ('_latestUnfiltered' in next) next._latestUnfiltered = fix(next._latestUnfiltered);
  return next;
}
